import React, { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import ChurchPlannerRightRail from './ChurchPlannerRightRail';
import { ChurchPlannerRailProvider } from './ChurchPlannerRailContext';
import { plannerColors } from './plannerTheme';
import { hasAnyPermission, Permission } from '../../utils/roles';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaPlus as FaPlusRaw,
} from 'react-icons/fa';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPlus: any = FaPlusRaw;

const BREAK_LG = '1100px';
const BREAK_MD = '960px';

/**
 * Horizontal bleed only: Layout `<Content>` has padding 2rem / 1rem; negative top margin
 * would pull this block over `<HeaderBar>` (rendered above `{children}`).
 */
const ModuleRoot = styled.div`
  margin-top: 0;
  margin-bottom: 0;
  margin-left: -2rem;
  margin-right: -2rem;
  width: calc(100% + 4rem);
  max-width: none;
  padding: 1.25rem 2rem;
  min-height: calc(100vh - 5rem);
  background: ${plannerColors.bg};
  color: ${plannerColors.text};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media (max-width: ${BREAK_MD}) {
    margin-left: -1rem;
    margin-right: -1rem;
    width: calc(100% + 2rem);
    padding: 1rem;
    min-height: auto;
  }
`;

const WorkspaceRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  min-width: 0;
  min-height: 0;
`;

const MainColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

/** ClassroomTeacherLayout-style top bar (card) */
const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: ${plannerColors.surface};
  border: 1px solid ${plannerColors.border};
  border-radius: 16px;
  flex-shrink: 0;

  @media (max-width: ${BREAK_MD}) {
    padding: 1rem;
  }
`;


const CenterTabs = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  min-width: 0;
  overflow-x: auto;
  gap: 0.15rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TopTab = styled(NavLink)`
  padding: 0.5rem 0.65rem;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Open Sans', sans-serif;
  color: ${plannerColors.muted};
  text-decoration: none;
  white-space: nowrap;
  border-radius: 8px;
  border-bottom: 2px solid transparent;

  &:hover {
    color: ${plannerColors.accent};
    background: ${plannerColors.surfaceHover};
  }

  &.active {
    color: ${plannerColors.accent};
    font-weight: 600;
    border-bottom-color: ${plannerColors.accent};
  }
`;

const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const TopActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.5rem 0.8rem;
  border: 1px solid ${({ $variant }) => ($variant === 'secondary' ? plannerColors.border : 'transparent')};
  border-radius: 8px;
  background: ${({ $variant }) => ($variant === 'secondary' ? plannerColors.surfaceHover : plannerColors.accent)};
  color: ${({ $variant }) => ($variant === 'secondary' ? plannerColors.text : '#1a1a1a')};
  font-size: 0.82rem;
  font-weight: 700;
  font-family: 'Open Sans', sans-serif;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    filter: brightness(1.05);
  }
`;

const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const AvatarLink = styled(NavLink)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid ${plannerColors.accentSoft};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.accent};
  font-size: 0.85rem;
  font-weight: 700;
  margin-left: 0.25rem;
`;

const BodyRow = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  align-items: flex-start;
  gap: 1.25rem;
  padding: 0;

  @media (max-width: ${BREAK_LG}) {
    flex-direction: column;
  }
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  padding-bottom: 4rem;
`;

const RailWrap = styled.div`
  width: 340px;
  flex-shrink: 0;

  @media (max-width: ${BREAK_LG}) {
    width: 100%;
  }
`;

const Fab = styled.button`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: ${plannerColors.accent};
  color: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);

  &:hover {
    filter: brightness(1.06);
  }
`;

const topTabs = [
  { to: '/church-planner/dashboard', label: 'Dashboard' },
  { to: '/church-planner/assignments', label: 'Planning', usePlanningMatch: true },
  { to: '/church-planner/my-assignments', label: 'My Assignments' },
  { to: '/church-planner/calendar', label: 'Calendar' },
  { to: '/church-planner/songs', label: 'Songs' },
  { to: '/church-planner/schedule', label: 'Schedules' },
  { to: '/church-planner/people', label: 'People' },
];

const ChurchPlannerLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dashboardServiceId, setDashboardServiceId] = useState<string | undefined>(undefined);
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const normalizedRoles = Array.isArray(roles)
    ? roles
        .filter((role): role is string => typeof role === 'string')
        .map((role) => role.trim().toLowerCase())
    : [];
  const canTeamLead = normalizedRoles.includes('service_planner_team_leader');
  const canCreatePlans = hasAnyPermission(roles, [Permission.CREATE_CHURCH_PLAN]);
  const canManagePlanner = hasAnyPermission(roles, [
    Permission.MANAGE_CHURCH_PLANNER_ROLES,
    Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS,
  ]) || canTeamLead;
  const canAccessSettings = canCreatePlans || canManagePlanner;
  const canViewPeopleTab = hasAnyPermission(roles, [Permission.MANAGE_CHURCH_PLANNER_ROLES]) || canTeamLead;
  const displayName =
    profile?.full_name?.trim() ||
    (user?.email as string | undefined)?.split('@')[0] ||
    'You';
  const initials = displayName
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const path = location.pathname;
  const showRightRail = !path.startsWith('/church-planner/settings');
  const matchPlanning =
    path.startsWith('/church-planner/assignments') ||
    path.startsWith('/church-planner/calendar') ||
    path.startsWith('/church-planner/schedule') ||
    path.startsWith('/church-planner/service-health') ||
    path.startsWith('/church-planner/activity') ||
    path.startsWith('/church-planner/songs') ||
    path.startsWith('/church-planner/people') ||
    path.startsWith('/church-planner/profile');

  const baseTabs = canViewPeopleTab
    ? topTabs
    : topTabs.filter((tab) => tab.to !== '/church-planner/people');

  const visibleTabs = baseTabs;

  return (
    <ChurchPlannerRailProvider value={{ dashboardServiceId, setDashboardServiceId }}>
      <ModuleRoot>
        <WorkspaceRow>
          <MainColumn>
            <TopBar>
              <CenterTabs>
                {visibleTabs.map((tab) => (
                  <TopTab
                    key={`${tab.to}-${tab.label}`}
                    to={tab.to}
                    className={({ isActive }) =>
                      tab.usePlanningMatch && matchPlanning ? 'active' : isActive ? 'active' : ''
                    }
                  >
                    {tab.label}
                  </TopTab>
                ))}
              </CenterTabs>
              <TopActions>
                {canCreatePlans ? (
                  <TopActionButton type="button" onClick={() => navigate('/church-planner/services/new')}>
                    New service
                  </TopActionButton>
                ) : null}
                {canAccessSettings ? (
                  <TopActionButton
                    type="button"
                    $variant="secondary"
                    onClick={() => navigate('/church-planner/settings')}
                  >
                    Settings
                  </TopActionButton>
                ) : null}
              </TopActions>
              <TopRight>
                <AvatarLink to="/church-planner/profile" title="Profile">
                  {initials}
                </AvatarLink>
              </TopRight>
            </TopBar>

            <BodyRow>
              <MainContent>
                <Outlet />
                <Fab type="button" aria-label="Add">
                  <FaPlus size={22} />
                </Fab>
              </MainContent>
              {showRightRail ? (
                <RailWrap>
                  <ChurchPlannerRightRail />
                </RailWrap>
              ) : null}
            </BodyRow>
          </MainColumn>
        </WorkspaceRow>
      </ModuleRoot>
    </ChurchPlannerRailProvider>
  );
};

export default ChurchPlannerLayout;
